import { Component, EventEmitter, Input, Output, OnInit, SimpleChanges } from '@angular/core';
import moment from 'moment';

@Component({
    selector: 'app-quarter-selection',
    templateUrl: './quarter-selection.component.html',
    styleUrls: ['./quarter-selection.component.scss'],
    standalone: false
})
export class QuarterSelectionComponent implements OnInit {
  @Input() selectedData: any;
  @Input() DateConfig: any;
  @Output() applyFilter = new EventEmitter<any>();
   minYear: number = 2020;
   maxYear: number =new Date().getFullYear();
  headerName:any='Date Selection'
  allYears: number[] = [];
  quarters: string[] = [];
  leftQuarters: string[] = [];
  rightQuarters: string[] = [];

  selectedStartQuarter: string | any 
  selectedEndQuarter: string | any 

  minQuarter = 'Q1 2020'; // Minimum quarter
  maxQuarter = 'Q4 2025'; // Maximum quarter

  startYear!: number;
  endYear!: number;

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['selectedData']?.currentValue!=changes['selectedData']?.previousValue) {
      // Set initial calendar views
      this.initializeYears();
      this.minYear=Number(moment(this.DateConfig.option.minDate).format("YYYY")) - 2;
      this.maxYear=Number(moment(this.DateConfig.option.maxDate).format("YYYY")) + 2;
      this.startYear = Number(moment(this.selectedData.from_date).format("YYYY"));
      this.quarters = this.generateQuarters(this.allYears);
      this.endYear = Number(moment(this.selectedData.to_date).format("YYYY")); + 2; // Default to 3-year range
      this.minQuarter = this.getQuarter(this.DateConfig.option.minDate);
      this.maxQuarter = this.getQuarter(this.DateConfig.option.maxDate);
      this.selectedStartQuarter = this.getQuarter(this.selectedData.from_date);
      this.selectedEndQuarter = this.getQuarter(this.selectedData.to_date);
      this.updateQuarters();
    }
  }

  getQuarter(dateStr: any) {
    const date = new Date(dateStr);
    const quarter = Math.floor((date.getMonth() + 3) / 3); // Get quarter (1-4)
    return `Q${quarter} ${date.getFullYear()}`;
  }
  // Update quarters for both left and right panels
  updateQuarters(): void {
    this.leftQuarters = this.generateQuartersForRange(this.startYear, this.startYear + 2);
    this.rightQuarters = this.generateQuartersForRange(this.endYear, this.endYear + 2);
  }

  // Generate quarters for a range of years
  generateQuartersForRange(startYear: number, endYear: number): string[] {
    const quarters: string[] = [];
    for (let year = startYear; year <= endYear; year++) {
      quarters.push(...['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${q} ${year}`));
    }
    return quarters;
  }

  // Move the start year range backward
  moveStartYearPrevious(): void {
    if (this.startYear > this.minYear) {
      this.startYear--;
      this.updateQuarters();
    }
  }

  // Move the start year range forward
  moveStartYearNext(): void {
    if (this.startYear + 2 < this.maxYear ) {
      this.startYear++;
      this.updateQuarters();
    }
  }

  // Move the end year range backward
  moveEndYearPrevious(): void {
    if (this.endYear > this.startYear + 2) {
      this.endYear--;
      this.updateQuarters();
    }
  }

  // Move the end year range forward
  moveEndYearNext(): void {
    if (this.endYear < this.maxYear - 2) {
      this.endYear++;
      this.updateQuarters();
    }
  }

  // Initialize the years array dynamically
  initializeYears(): void {
    const currentYear = new Date().getFullYear();
    this.allYears = Array.from({ length: this.maxYear - this.minYear + 1 }, (_, i) => this.minYear + i);
  }

  // Generate quarters for the given years
  generateQuarters(years: number[]): string[] {
    return years.flatMap((year) => ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${q} ${year}`));
  }

  selectQuarter(quarter: string, type: string): void {
    if (this.isDisabled(quarter)) return;
  
    const quarterIndex = this.quarters.indexOf(quarter);
  
    if (type === 'start') {
      // Set the selected start quarter
      this.selectedStartQuarter = quarter;
  
      // Ensure that the end quarter is not before the newly selected start quarter
      if (this.selectedEndQuarter) {
        const endIndex = this.quarters.indexOf(this.selectedEndQuarter);
        if (quarterIndex > endIndex) {
          // Reset the end quarter if it's before the new start quarter
          this.selectedEndQuarter = null;
        }
      }
    } else if (type === 'end') {
      if (!this.selectedStartQuarter) {
        // If no start quarter is selected, set it instead
        this.selectedStartQuarter = quarter;
      } else {
        const startIndex = this.quarters.indexOf(this.selectedStartQuarter);
        if (quarterIndex >= startIndex) {
          // Only set end quarter if it's on or after the start
          this.selectedEndQuarter = quarter;
        } else {
          // If selected end is before start, reset both
          this.selectedStartQuarter = null;
          this.selectedEndQuarter = quarter;
        }
      }
    }
  
    // Optional: add additional validations or callbacks here
  }
  


  get canMoveStartPrevious(): boolean {
    return this.startYear > this.minYear;
  }

  get canMoveStartNext(): boolean {
    return this.startYear < this.maxYear;
  }

  get canMoveEndPrevious(): boolean {
    return this.endYear > this.startYear;
  }

  get canMoveEndNext(): boolean {
    return this.endYear < this.maxYear;
  }
  // Check if the quarter is selected
  isSelected(quarter: string): boolean {
    return this.selectedStartQuarter === quarter || this.selectedEndQuarter === quarter;
  }

  // Check if the quarter is within the selected range
  isInRange(quarter: string): boolean {
    if (!this.selectedStartQuarter || !this.selectedEndQuarter) return false;
    const startIndex = this.quarters.indexOf(this.selectedStartQuarter);
    const endIndex = this.quarters.indexOf(this.selectedEndQuarter);
    const currentIndex = this.quarters.indexOf(quarter);
    return currentIndex >= startIndex && currentIndex <= endIndex;
  }

  // Check if the quarter is disabled based on min/max quarter range
  isDisabled(quarter: string): boolean {
    const quarterIndex = this.quarters.indexOf(quarter);
    const minIndex = this.quarters.indexOf(this.minQuarter);
    const maxIndex = this.quarters.indexOf(this.maxQuarter);
    return quarterIndex < minIndex || quarterIndex > maxIndex;
  }

  // Get the start date of the selected quarter
  getQuarterStartDate(quarter: string): Date {
    const [q, year] = quarter.split(' ');
    const dates: any = { Q1: '01/01', Q2: '04/01', Q3: '07/01', Q4: '10/01' };
    return new Date(`${dates[q]}/${year}`);
  }

  // Get the end date of the selected quarter
  getQuarterEndDate(quarter: string): Date {
    const [q, year] = quarter.split(' ');
    const dates: any = { Q1: '03/31', Q2: '06/30', Q3: '09/30', Q4: '12/31' };
    return new Date(`${dates[q]}/${year}`);
  }

  // Clear selected dates
  clearDates(): void {
    this.selectedStartQuarter = null;
    this.selectedEndQuarter = null;
  }

  // Apply selected date range and emit the filter event
  applyDates(): void {
    if (this.selectedStartQuarter && this.selectedEndQuarter) {
      const input = {
        from_date: this.getQuarterStartDate(this.selectedStartQuarter),
        to_date: this.getQuarterEndDate(this.selectedEndQuarter),
      };
      this.applyFilter.emit({
        from_date: this.selectedStartQuarter,
        to_date: this.selectedEndQuarter,
      });
    } else {
      alert('Please select both start and end quarters.');
    }
     //menu close
  document.getElementById("du-page")?.click()
  }

  // Generate quarter objects for the selected range
  generateQuarterObjects(input: { from_date: Date; to_date: Date }): any[] {
    const quartersTemplate: any = {
      Q1: { months: ['Jan', 'Feb', 'Mar'], monthFound: '1,2,3', from_date: '01-01', to_date: '03-31' },
      Q2: { months: ['Apr', 'May', 'Jun'], monthFound: '4,5,6', from_date: '04-01', to_date: '06-30' },
      Q3: { months: ['Jul', 'Aug', 'Sep'], monthFound: '7,8,9', from_date: '07-01', to_date: '09-30' },
      Q4: { months: ['Oct', 'Nov', 'Dec'], monthFound: '10,11,12', from_date: '10-01', to_date: '12-31' },
    };

    const result = [];
    const startYear = input.from_date.getFullYear();
    const endYear = input.to_date.getFullYear();

    for (let year = startYear; year <= endYear; year++) {
      for (const quarterKey in quartersTemplate) {
        const quarterData = quartersTemplate[quarterKey];
        const quarterStartDate = new Date(`${year}-${quarterData.from_date}`);
        const quarterEndDate = new Date(`${year}-${quarterData.to_date}`);

        const adjustedFromDate = quarterStartDate < input.from_date ? input.from_date : quarterStartDate;
        const adjustedToDate = quarterEndDate > input.to_date ? input.to_date : quarterEndDate;

        result.push({
          value: quarterKey,
          months: quarterData.months,
          monthFound: quarterData.monthFound,
          'quarter,year': `${quarterKey} ${year}`,
          from_date: adjustedFromDate.toISOString().split('T')[0],
          to_date: adjustedToDate.toISOString().split('T')[0],
          year: year,
        });
      }
    }

    return result;
  }
}